/**
 * Parses the module XPath to extract the module XPath.
 * @param xpath - The XPath string to parse.
 * @return The module XPath string, which includes the module ID and the path up to the next slash after the module ID.
 */
export const parseModuleXPath = (xpath: string): string => {
  // Extract module ID from the XPath
  const moduleRegex = /modul\[@id="([^"]+)"]/;
  const moduleMatch = xpath.match(moduleRegex);

  // Find the index of the module match in the XPath
  const moduleMatchIndex = moduleMatch ? xpath.indexOf(moduleMatch[0]) : -1;
  let slashAfterModuleIndex = -1;
  if (moduleMatch) {
    slashAfterModuleIndex = moduleMatchIndex > -1 ?
      xpath.indexOf('/', moduleMatchIndex + moduleMatch[0].length) : -1;
  }

  // Create module XPath
  return moduleMatchIndex > -1 ?
    xpath.substring(0, slashAfterModuleIndex > -1 ? slashAfterModuleIndex : xpath.length) : '';
}

/**
 * Extracts category type and ID from an XPath string.
 * @param xpath - The XPath string to parse.
 * @return An object containing the category type and ID, or null if not found.
 */
export const extractCategoryInfo = (xpath: string): { type: string, id: string } | null => {
  const categoryRegex = /\/([^\/\[]+)\[@id="([^"]+)"]\/element\[@id="/;
  const match = xpath.match(categoryRegex);

  if (match) {
    return {
      type: match[1] || '',
      id: match[2] || ''
    };
  }

  return null;
}

/**
 * Retrieves the category name from an XPath string.
 * @param xpath - The XPath string to extract the category name from.
 * @param useNameElement - Boolean indicating whether to use a name element value (if provided)
 * @param nameElementValue - Optional string value from a name element to use in the category name
 * @return The category name as a string
 */
export const getCategoryNameForXPath = (
  xpath: string,
  useNameElement: boolean = false,
  nameElementValue?: string
): string => {
  const categoryRegex = /\/([^\/\[]+)\[@id="([^"]+)"]\/element\[@id="/;
  const fallbackRegex = /\/([^\/]+)\/element\[@id="/;

  let categoryName = 'general';

  // Check for category pattern in XPath
  const categoryMatch = xpath.match(categoryRegex);
  if (categoryMatch) {
    const categoryType = categoryMatch[1] || '';  // "connection", "realm", etc.
    const categoryId = categoryMatch[2] || '';    // The ID, usually a number

    // Use name element value if provided and requested
    if (useNameElement && nameElementValue) {
      categoryName = `${categoryType} ${nameElementValue}`;
    } else {
      categoryName = `${categoryType} ${categoryId}`;
    }
  } else {
    // Fallback to a simpler extraction if the above pattern fails
    const fallbackMatch = xpath.match(fallbackRegex);
    if (fallbackMatch) {
      categoryName = fallbackMatch[1];
    }
  }
  return categoryName;
}

/**
 * Extracts the category XPath from a full XPath string.
 * @param xpath - The full XPath string to extract the category XPath from.
 * @return The category XPath string, which is the part of the XPath before the '/element' segment.
 */
export const getCategoryXpath = (xpath: string): string => {
  return xpath.substring(0, xpath.indexOf('/element'));
}